import React, {
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import IUser from '../../../common/interfaces/IUser';
import UserService from '../../../common/services/user';
import WelcomeConstants from '../../../pages/welcome/constants';
import HomeConstants from '../../../pages/home/constants';
import IAuthContextData from './Interfaces/IAuthContextData';
import { realmByUser, userToNumber } from '../../../utils/realm-utils';
import { decodeToken } from '../../../utils/tokenDecoder';
import { SULAHUB_ROLES } from '../../../common/constants/roles';
import ConfigurationManager from '../../../utils/configurationManager';
import {
    getIdentityToken,
    getToken,
    getTokenByRefreshToken,
} from '../../../common/services/sulahub-auth/sulahub-auth-service';

const AuthContext = React.createContext<IAuthContextData>(
    {} as IAuthContextData,
);

function AuthProvider({ children }: PropsWithChildren) {
    const [loggedUser, setLoggedUser] = useState<IUser | null>(
        UserService.getUserFromStorage(),
    );
    const [refreshFailed, setRefreshFailed] = useState(false);
    const [isFetchingToken, setIsFetchingToken] = useState(true);
    const [searchParams] = useSearchParams();
    const intervalref = useRef<number | null>();
    const navigate = useNavigate();

    const stopInterval = useCallback(() => {
        if (intervalref.current) {
            window.clearInterval(intervalref.current);
            intervalref.current = null;
        }
    }, []);

    const signOut = useCallback(() => {
        localStorage.removeItem('DisplayName');
        sessionStorage.clear();
        stopInterval();
        window.location.href = WelcomeConstants.default;
    }, [stopInterval]);

    const startInterval = useCallback(() => {
        if (!intervalref.current) {
            const interval = getExpirateIn();

            intervalref.current = window.setInterval(async () => {
                setIsFetchingToken(true);

                const refreshTokenfromStorage =
                    sessionStorage.getItem('RefreshToken');

                const refreshToken = refreshTokenfromStorage ?? '';

                const user = sessionStorage.getItem('UserType') ?? '';

                const resultRefresh = await getTokenByRefreshToken(
                    refreshToken,
                    userToNumber(user),
                );
                if (resultRefresh?.response) {
                    const resultIdentity = await getIdentityToken(
                        loggedUser?.country,
                    );
                    if (resultIdentity?.error) {
                        setRefreshFailed(true);
                    }
                } else if (resultRefresh?.error) {
                    setRefreshFailed(true);
                }

                setIsFetchingToken(false);
            }, interval);
        }
    }, [loggedUser]);

    useEffect(() => {
        if (loggedUser) {
            startInterval();
        }
    }, [loggedUser, startInterval]);

    useEffect(() => {
        if (refreshFailed) {
            signOut();
        }
    }, [refreshFailed, signOut]);

    const getExpirateIn = () => {
        const accessToken = sessionStorage.getItem('AccessToken')!;

        const { exp } = decodeToken(accessToken);
        const now = new Date();
        const expires = new Date(Number(exp) * 1000);

        return expires.valueOf() - now.valueOf() - 60 * 1000;
    };

    const isAuthorized = useCallback(
        (componentAccessRole: string | string[]) => {
            const accessRoles =
                typeof componentAccessRole === 'string'
                    ? [componentAccessRole]
                    : componentAccessRole;

            return accessRoles.some(role => loggedUser?.roles?.includes(role));
        },
        [loggedUser?.roles],
    );

    const isThirdUser = useCallback(
        () =>
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_USER,
            ]),
        [isAuthorized],
    );

    const isFirstAccess = useCallback(() => {
        const userRoles = loggedUser?.roles;

        return (
            userRoles === undefined ||
            userRoles?.filter(role => role !== 'default').length === 0
        );
    }, [loggedUser?.roles]);

    const loginByCode = useCallback(async () => {
        setIsFetchingToken(true);

        stopInterval();
        const code = searchParams.get('code');
        const user = sessionStorage.getItem('UserType');

        if (code != null && user != null) {
            const { REDIRECT_URI } =
                await ConfigurationManager.getInstance().getConfigFile(
                    realmByUser(user),
                );

            const result = await getToken(
                code,
                REDIRECT_URI,
                userToNumber(user),
            );

            if (result?.response) {
                const identityResult = await getIdentityToken();
                if (identityResult?.response) {
                    const storagedUser = UserService.getUserFromStorage();

                    if (storagedUser) {
                        setLoggedUser(storagedUser);
                        setIsFetchingToken(false);
                        startInterval();
                        navigate(HomeConstants.PATH);
                    } else {
                        navigate(WelcomeConstants.default, {
                            state: 'login_failed',
                        });
                    }
                } else if (identityResult?.error) {
                    navigate(WelcomeConstants.default, {
                        state: 'login_failed',
                    });
                } else if (result?.error) {
                    navigate(WelcomeConstants.default, {
                        state: 'login_failed',
                    });
                }
            }
        } else {
            window.location.href = WelcomeConstants.default;
        }

        setIsFetchingToken(false);
    }, [searchParams, navigate, startInterval, stopInterval]);

    const refreshIdentityToken = useCallback(async (country: string) => {
        setIsFetchingToken(true);

        await getIdentityToken(country);

        const storagedUser = UserService.getUserFromStorage();

        setLoggedUser(storagedUser);
        setIsFetchingToken(false);
    }, []);

    const setUsername = useCallback(
        (username: string) => {
            setLoggedUser({ ...loggedUser!, name: username });
        },
        [loggedUser],
    );

    const providedProperties: IAuthContextData =
        useMemo((): IAuthContextData => {
            return {
                user: loggedUser,
                isFetchingToken,
                signOut,
                loginByCode,
                isAuthorized,
                isFirstAccess,
                isThirdUser,
                refreshIdentityToken,
                setUsername,
            };
        }, [
            isAuthorized,
            isFetchingToken,
            isFirstAccess,
            isThirdUser,
            loggedUser,
            loginByCode,
            refreshIdentityToken,
            signOut,
            setUsername,
        ]);

    return (
        <AuthContext.Provider value={providedProperties}>
            {children}
        </AuthContext.Provider>
    );
}

const useAuth = (): IAuthContextData => {
    return useContext(AuthContext);
};

export default (() => {
    return {
        AuthContext,
        AuthProvider,
        useAuth,
    };
})();
